/**
 * The Schedule page: keep the day's running orders, pick the one being run, watch the countdown.
 *
 * Two halves, both always visible:
 *   - The countdown panel reads the SAVED active plan and shows the time to the next event, big.
 *   - The editor below holds a DRAFT of the whole plan library. Nothing you type changes the
 *     countdown until you press Save — a half-typed row must not blank out the readout everyone
 *     in the room is watching.
 *
 * A SCHEDULE IS A DAILY PLAN, NOT A DATE'S SCHEDULE. The same running order gets used day after
 * day, so a project keeps a LIBRARY of named plans ("Race day", "Training", "Tow test"), one of
 * which is the one being run. Nothing here reads the sidebar's date, and the page therefore works
 * identically from every sidebar mode — project, day, dataset and live — which is what a `tools`
 * page has to do.
 *
 * THE CLOCK IS THE WALL CLOCK, not playback time. A plan counts down to real events (dock out,
 * warning signal, debrief), so it must keep running when playback is paused, scrubbed back or
 * replaying. `selectedTime()` is deliberately not read here. The plan's times are resolved against
 * TODAY, and `todayYmd` rolls the countdown over at local midnight.
 *
 * Storage is a single project object — see services/scheduleService.ts. It is shared by the whole
 * project, so one person saves and everybody's page picks it up on its next poll.
 */

import { onMount, onCleanup, createSignal, createMemo, createEffect, Show, For } from 'solid-js';
import { createStore, unwrap } from 'solid-js/store';
import { FiPlus, FiTrash2, FiSettings, FiCopy } from 'solid-icons/fi';
import { persistantStore } from '@store/persistantStore';
import { error as logError, debug as logDebug } from '@utils/console';
import { logPageLoad } from '@utils/logging';
import {
    DEFAULT_PLAN_NAME,
    formatCountdown,
    formatElapsed,
    isRowIncomplete,
    localDayYmd,
    newScheduleItemId,
    newSchedulePlan,
    schedulePlansEqual,
    shiftScheduleItems,
    resolveScheduleStatus,
    retimeScheduleItem,
    resolveUrgency,
    uniquePlanName,
    type ScheduleItem,
    type SchedulePlan,
} from '@utils/scheduleCountdown';
import { fetchSchedule, saveSchedule } from '@services/scheduleService';

const { selectedClassName, selectedProjectId } = persistantStore;

/** Countdown tick. One second is the display resolution, so faster buys nothing. */
const TICK_MS = 1_000;

/**
 * How often to re-read the saved library from the project object.
 *
 * The schedule is shared, and someone else saving a change is the only way it moves, so this is a
 * slow poll rather than a live stream. 30 s is well inside the time it takes to react to a
 * schedule change on the water.
 */
const POLL_MS = 30_000;

function newDraftRow(time = '', name = ''): ScheduleItem {
    return { id: newScheduleItemId(), time, name };
}

/** A deep copy, so the draft can never write through into the saved library behind it. */
function clonePlan(plan: SchedulePlan): SchedulePlan {
    return { id: plan.id, name: plan.name, items: plan.items.map((i) => ({ ...i })) };
}

export interface ScheduleReportSectionProps {
    /**
     * Nothing is read from props.
     *
     * Kept open because a tools page is mounted from one DB row in every sidebar mode and the
     * loaders hand their components whatever context they have. This page needs none of it: the
     * project comes from the store and the day is always today.
     */
    [key: string]: unknown;
}

export default function ScheduleReportSection(_props: ScheduleReportSectionProps) {
    /**
     * The project the library is stored against, or null when none is picked yet.
     *
     * Not inlined at each call site because `selectedProjectId` has TWO empty values: it is `0`
     * before a project is chosen, and a pop-out sets it from its URL, so it can also arrive as a
     * STRING. A plain null/undefined check let both through and would have written the schedule
     * to project 0.
     */
    const projectRef = () => {
        const className = selectedClassName?.();
        const projectId = Number(selectedProjectId?.() ?? 0);
        if (!className || !Number.isFinite(projectId) || projectId <= 0) {return null;}
        return { className, projectId };
    };

    /** The saved library: what the countdown runs on. */
    const [appliedPlans, setAppliedPlans] = createSignal<SchedulePlan[]>([]);
    const [appliedActiveId, setAppliedActiveId] = createSignal<string | null>(null);
    const [appliedAt, setAppliedAt] = createSignal<string | null>(null);

    /**
     * The editor's working copy — a STORE, not a signal.
     *
     * This is load-bearing, not a style choice. `<For>` keys its rows by reference, so the
     * signal version (which rebuilt the edited row with `{...row, ...patch}` on every keystroke)
     * handed `For` a brand-new object each time, and `For` responded by disposing that row's
     * <tr> and building a fresh one. The <input> the user was typing into was destroyed and
     * replaced mid-word, losing focus and the caret after every single character.
     *
     * A store mutates the row in place, so the row's proxy identity never changes, `For` leaves
     * the DOM alone, and the change is tracked per PROPERTY: typing a name does not even
     * re-evaluate the time input beside it.
     */
    const [draft, setDraft] = createStore<{ plans: SchedulePlan[] }>({ plans: [] });

    /**
     * The plan on screen — and the one that becomes the running plan on Save.
     *
     * One signal does both jobs deliberately. A separate "which plan is active" control would
     * make it possible to save a library while staring at a plan that is not the one the countdown
     * will use, which is exactly the confusion this page exists to prevent.
     */
    const [selectedPlanId, setSelectedPlanId] = createSignal<string | null>(null);

    const planIndex = () => draft.plans.findIndex((p) => p.id === selectedPlanId());
    const selectedPlan = () => {
        const i = planIndex();
        return i >= 0 ? draft.plans[i] : null;
    };
    /** The rows of the plan being edited. Empty when the library has no plan in it yet. */
    const draftItems = () => selectedPlan()?.items ?? [];

    /** Name of the plan the countdown is running, for the read-only header. */
    const appliedActiveName = () =>
        appliedPlans().find((p) => p.id === appliedActiveId())?.name ?? '';

    /** The rows the countdown runs: the SAVED active plan, never the draft. */
    const appliedItems = () =>
        appliedPlans().find((p) => p.id === appliedActiveId())?.items ?? [];

    /**
     * The time a row held when the user focused its time field.
     *
     * Needed to work out how far the event moved, which is what the following events are carried
     * by. It cannot be read at commit time, because by then the row already holds the new value.
     */
    let timeBeforeEdit: { id: string; time: string } | null = null;

    /**
     * The row the caret is currently in, or null.
     *
     * Validation skips it. A half-typed row is not a mistake, it is a row in progress, and
     * warning about it while the user is still typing is just noise.
     */
    const [editingRowId, setEditingRowId] = createSignal<string | null>(null);

    /**
     * Edit mode. The page is a read-only countdown until you ask to change it.
     *
     * Starts false and is switched on once after the first load if the project has no plan yet -
     * see the effect below. Without that, a new project would show nothing but an empty countdown
     * and a gear that only appears on hover, which reads like the page failed to load.
     */
    const [editing, setEditing] = createSignal(false);
    /** Guards the one-shot auto-open above, so re-entering edit mode is never undone by a poll. */
    let hasAutoOpenedEditor = false;

    const [loading, setLoading] = createSignal(true);
    const [loadError, setLoadError] = createSignal<string | null>(null);
    const [saveError, setSaveError] = createSignal<string | null>(null);
    const [saving, setSaving] = createSignal(false);
    const [savedFlash, setSavedFlash] = createSignal(false);
    /** Bumped by the poll so the load effect re-runs without anything else having changed. */
    const [refreshNonce, setRefreshNonce] = createSignal(0);

    /** Whole-plan offset, in minutes, and the reason the last attempt was refused. */
    const [offsetMinutes, setOffsetMinutes] = createSignal<number>(5);
    const [offsetError, setOffsetError] = createSignal<string | null>(null);

    const [nowMs, setNowMs] = createSignal(Date.now());

    /**
     * Today, as a local "YYYYMMDD" — the day the plan's times are resolved against.
     *
     * The LOCAL day, not the UTC day the parquet families are partitioned by: schedule times are
     * local wall clock (see utils/scheduleCountdown.ts), so the rollover must happen at local
     * midnight or a plan looked at late in the evening would start counting to the wrong day.
     *
     * It is a separate signal rather than being derived from the 1 Hz `nowMs` because the status
     * memo would then re-resolve every single second. This is written on every tick too, but it
     * holds a string, so Solid's default equality means it only notifies when the day rolls over.
     */
    const [todayYmd, setTodayYmd] = createSignal(localDayYmd());

    /** The page root; its measured height is what bounds the scrolling body. */
    let rootEl: HTMLDivElement | undefined;

    let abortController: AbortController | null = null;
    /** Guards against a slow response overwriting a newer one. */
    let fetchGeneration = 0;
    let savedFlashTimer: ReturnType<typeof setTimeout> | null = null;

    const status = createMemo(() => resolveScheduleStatus(appliedItems(), todayYmd(), nowMs()));
    const urgency = createMemo(() => resolveUrgency(status().msToNext));

    /**
     * Anything that would store differently: the plans themselves, or which one is running.
     *
     * The second half matters on its own. Switching the dropdown to another plan changes nothing
     * inside any plan, but it IS the change "today we are running this one instead", and the user
     * has to be able to save it.
     */
    const isDirty = createMemo(
        () =>
            !schedulePlansEqual(draft.plans, appliedPlans()) ||
            selectedPlanId() !== appliedActiveId(),
    );

    /**
     * Rows that would be dropped on Save, so the page can say so instead of silently binning them.
     *
     * The row being typed into is EXCLUDED. Every half-finished row is momentarily incomplete —
     * the first letter of an event name lands before its time does — so counting the focused row
     * made the warning appear on the first keypress and sit there for the whole of the typing.
     * It reappears the moment focus leaves, which is when it is actually actionable.
     */
    const invalidDraftCount = createMemo(() => {
        const focused = editingRowId();
        return draftItems().filter((row) => {
            if (row.id === focused) {return false;}
            return isRowIncomplete(row);
        }).length;
    });

    /**
     * Give the page an explicit pixel height: from its own top edge to the bottom of the viewport.
     *
     * WHY NOT CSS. Every percentage or flex chain here has to terminate at an ancestor with a
     * definite height, and in this container it does not - the page kept sizing to its content, so
     * adding an event expanded the box instead of scrolling it, and the last rows were clipped off
     * the bottom of the window. Measuring sidesteps the whole chain: whatever the ancestors do,
     * `innerHeight - top` is exactly the space that is actually visible.
     *
     * No feedback loop: the root's `top` is fixed by what renders ABOVE it, which this never
     * touches, so writing its height cannot move it.
     */
    const fitToViewport = () => {
        if (!rootEl) {return;}
        const top = rootEl.getBoundingClientRect().top;
        const available = Math.max(200, Math.round(window.innerHeight - top));
        const next = `${available}px`;
        // Only write on a real change; an unconditional write every observer tick would dirty
        // layout once a frame for nothing.
        if (rootEl.style.height !== next) {
            rootEl.style.height = next;
            rootEl.style.maxHeight = next;
        }
    };

    onMount(() => {
        void logPageLoad('ScheduleReportSection.tsx', 'Schedule', 'Loaded');

        // First measurement after the browser has laid the page out once; measuring inside onMount
        // reads a top of 0 while the tree is still being attached.
        requestAnimationFrame(fitToViewport);
        window.addEventListener('resize', fitToViewport);

        // The SelectionBanner above this page appears and disappears with the selection, which
        // moves our top edge without a resize event. Watching the parent catches that, and also
        // the split-view divider being dragged.
        let parentObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined' && rootEl?.parentElement) {
            parentObserver = new ResizeObserver(() => fitToViewport());
            parentObserver.observe(rootEl.parentElement);
        }
        onCleanup(() => {
            window.removeEventListener('resize', fitToViewport);
            parentObserver?.disconnect();
        });

        const tick = setInterval(() => {
            const ms = Date.now();
            setNowMs(ms);
            // No-op unless the local day actually changed - see todayYmd.
            setTodayYmd(localDayYmd(ms));
        }, TICK_MS);
        const poll = setInterval(() => setRefreshNonce((n) => n + 1), POLL_MS);
        // Registered here, not returned: Solid discards a value returned from onMount, so a
        // `return () => ...` cleanup would never run and both timers would outlive the page.
        onCleanup(() => {
            clearInterval(tick);
            clearInterval(poll);
            if (savedFlashTimer) {clearTimeout(savedFlashTimer);}
            abortController?.abort();
            abortController = null;
        });
    });

    /** Load the library whenever the project changes, and on each poll. */
    createEffect(() => {
        const project = projectRef();
        // Read in the tracked scope: nothing after the first await is reactive.
        void refreshNonce();

        if (!project) {
            setAppliedPlans([]);
            setAppliedActiveId(null);
            setLoading(false);
            return;
        }
        const { className, projectId } = project;

        const gen = ++fetchGeneration;
        abortController?.abort();
        const controller = new AbortController();
        abortController = controller;

        void (async () => {
            try {
                const library = await fetchSchedule({ className, projectId }, controller.signal);
                if (gen !== fetchGeneration) {return;}

                // Measured BEFORE the applied signals move. Afterwards `isDirty` would be
                // comparing the draft against the library that just arrived, so a remote change
                // made by someone else would itself read as local unsaved work and the editor
                // would never update.
                const hadUnsavedEdits = isDirty();

                setAppliedPlans(library.plans);
                setAppliedActiveId(library.activePlanId);
                setAppliedAt(library.updated_at ?? null);
                setLoadError(null);

                // Only adopt the fetched library into the editor when the user has no unsaved
                // work. Otherwise the 30 s poll would wipe out whatever they are halfway through
                // typing — the single worst thing this page could do.
                if (!hadUnsavedEdits) {
                    // Replace only when the content actually differs. A blind replace every 30 s
                    // rebuilt every row - the <For> keys change - which threw focus out of any
                    // field the user had clicked into but not yet typed in.
                    if (!schedulePlansEqual(library.plans, draft.plans)) {
                        setDraft('plans', library.plans.map(clonePlan));
                    }
                    // Follow the library's idea of which plan runs only when the one on screen has
                    // gone: moving someone to a different plan under their eyes is worse than
                    // leaving them on the one they were reading.
                    if (!library.plans.some((p) => p.id === selectedPlanId())) {
                        setSelectedPlanId(library.activePlanId);
                    }
                }
            } catch (err) {
                if ((err as { name?: string })?.name === 'AbortError') {return;}
                if (gen !== fetchGeneration) {return;}
                logError('Schedule: failed to load the schedule', err as never);
                setLoadError('Could not load this project’s schedule.');
            } finally {
                if (gen === fetchGeneration) {setLoading(false);}
            }

            // One-shot: a project with no plan at all opens straight into the editor with an empty
            // plan ready to type into, because a countdown with nothing to count down to and
            // no visible way in looks like a broken page. Guarded by a flag rather than by
            // `editing()` so the 30 s poll can never re-open the editor after the user closed it.
            if (gen === fetchGeneration && !hasAutoOpenedEditor) {
                hasAutoOpenedEditor = true;
                if (draft.plans.length === 0) {
                    const first = newSchedulePlan(DEFAULT_PLAN_NAME);
                    setDraft('plans', [first]);
                    setSelectedPlanId(first.id);
                    setEditing(true);
                }
            }
        })();
    });

    // ------------------------------------------------------------------ plan-level operations
    // All of these edit the DRAFT only. No plan is created, renamed or destroyed in the database
    // until Save, which is what makes Cancel a complete undo for a whole session of edits.

    const planNames = () => draft.plans.map((p) => p.name);

    const createPlan = () => {
        const plan = newSchedulePlan(uniquePlanName('New plan', planNames()));
        setDraft('plans', (plans) => [...plans, plan]);
        setSelectedPlanId(plan.id);
        setEditingRowId(null);
        setSaveError(null);
        setOffsetError(null);
    };

    /** Copy the plan on screen, rows and all — the "same again, with a tweak" case. */
    const duplicatePlan = () => {
        const source = selectedPlan();
        if (!source) {return;}
        // Fresh row ids: the copies are new rows, and sharing ids with the source would make the
        // editor's `row.id === id` writes hit both plans at once.
        const copy = newSchedulePlan(
            uniquePlanName(`${source.name} copy`, planNames()),
            source.items.map((i) => newDraftRow(i.time, i.name)),
        );
        setDraft('plans', (plans) => [...plans, copy]);
        setSelectedPlanId(copy.id);
        setEditingRowId(null);
        setSaveError(null);
        setOffsetError(null);
        logDebug('Schedule: copied a plan', { from: source.name, to: copy.name });
    };

    const deletePlan = () => {
        const index = planIndex();
        if (index < 0) {return;}
        const remaining = draft.plans.filter((_, i) => i !== index).map(clonePlan);
        setDraft('plans', remaining);
        // Land on the neighbour rather than on nothing, so the editor keeps a plan in it whenever
        // one is left.
        setSelectedPlanId(remaining[Math.min(index, remaining.length - 1)]?.id ?? null);
        setEditingRowId(null);
        setSaveError(null);
        setOffsetError(null);
    };

    const renamePlan = (name: string) => {
        const index = planIndex();
        if (index < 0) {return;}
        setDraft('plans', index, 'name', name);
        setSaveError(null);
    };

    // ------------------------------------------------------------------- row-level operations

    /**
     * Write one field of one row IN PLACE.
     *
     * `setDraft('plans', i, 'items', matcher, field, value)` leaves the row's proxy identity
     * untouched, which is what keeps the <input> alive under the caret. Rebuilding the row object
     * here would undo the whole point of the store.
     */
    const updateRow = (id: string, field: 'time' | 'name', value: string) => {
        const index = planIndex();
        if (index < 0) {return;}
        setDraft('plans', index, 'items', (row) => row.id === id, field, value);
        setSaveError(null);
    };

    /**
     * Commit a time change and carry every later event with it.
     *
     * Bound to `change`, not `input`. An <input type="time"> fires `input` once for the hour and
     * again for the minute, so cascading per keystroke would shift the rest of the day twice by
     * two different amounts on the way to one edit. `change` fires once, when the value settles.
     */
    const commitTimeEdit = (id: string, newTime: string) => {
        const before = timeBeforeEdit;
        timeBeforeEdit = null;
        if (!before || before.id !== id) {return;}
        const index = planIndex();
        if (index < 0) {return;}

        const result = retimeScheduleItem(draftItems(), id, before.time, newTime);
        setDraft('plans', index, 'items', result.items);
        // A failed cascade is not a failed edit - the direct change is in `items` either way.
        setOffsetError(result.ok ? null : (result.reason ?? null));
    };

    const addRow = () => {
        const index = planIndex();
        if (index < 0) {return;}
        setDraft('plans', index, 'items', (rows) => [...rows, newDraftRow()]);
        setSaveError(null);
    };

    const removeRow = (id: string) => {
        const index = planIndex();
        if (index < 0) {return;}
        if (editingRowId() === id) {setEditingRowId(null);}
        setDraft('plans', index, 'items', (rows) => rows.filter((row) => row.id !== id));
        setSaveError(null);
    };

    /** Throw the whole draft library away and start again from what is saved. */
    const revertDraft = () => {
        setEditingRowId(null);
        setDraft('plans', appliedPlans().map(clonePlan));
        setSelectedPlanId(appliedActiveId());
        setSaveError(null);
        setOffsetError(null);
    };

    /** Leave edit mode without keeping anything typed since the last save. */
    const cancelEditing = () => {
        revertDraft();
        setEditing(false);
    };

    /**
     * Move the whole plan by the offset box, in the given direction.
     *
     * This edits the DRAFT, not the saved plan, so a postponement follows the same
     * review-then-Save path as any other change - you can see where everything landed, and undo
     * it with Cancel, before anyone else's page picks it up.
     */
    const shiftAll = (direction: 1 | -1) => {
        const index = planIndex();
        if (index < 0) {return;}
        const minutes = Math.trunc(Number(offsetMinutes()));
        if (!Number.isFinite(minutes) || minutes === 0) {
            setOffsetError('Enter a number of minutes to shift by.');
            return;
        }
        const result = shiftScheduleItems(draftItems(), direction * Math.abs(minutes));
        if (!result.ok) {
            setOffsetError(result.reason ?? 'That offset would move an event outside the day.');
            return;
        }
        setOffsetError(null);
        setSaveError(null);
        // Whole-list replace, not per-row writes: every row's time changes, so there is no DOM
        // to preserve and the simpler call is the honest one.
        setDraft('plans', index, 'items', result.items);
    };

    const savePlans = async () => {
        const project = projectRef();
        if (!project) {
            // The day always resolves (it is today), so the project is the only thing that can be
            // missing here — say so, rather than making the user guess which half it is.
            setSaveError('No project selected. Pick a project in the sidebar first.');
            return;
        }
        const { className, projectId } = project;

        setSaving(true);
        setSaveError(null);
        try {
            const result = await saveSchedule(
                { className, projectId },
                unwrap(draft.plans),
                selectedPlanId(),
            );
            if (result.ok) {
                // Adopt what was actually stored, not the draft: the save drops blank rows and
                // sorts, so anything else would leave the page permanently "dirty".
                setAppliedPlans(result.saved);
                setAppliedActiveId(result.activePlanId);
                setDraft('plans', result.saved.map(clonePlan));
                setSelectedPlanId(result.activePlanId);
                setAppliedAt(new Date().toISOString());
                setEditing(false);
                setEditingRowId(null);
                setOffsetError(null);
                setSavedFlash(true);
                if (savedFlashTimer) {clearTimeout(savedFlashTimer);}
                savedFlashTimer = setTimeout(() => setSavedFlash(false), 1600);
                logDebug('Schedule: saved', {
                    plans: result.saved.length,
                    active: result.activePlanId,
                });
            } else {
                setSaveError(result.message ?? 'Failed to save the schedule.');
            }
        } catch (err) {
            if ((err as { name?: string })?.name === 'AbortError') {return;}
            logError('Schedule: failed to save the schedule', err as never);
            setSaveError('Failed to save the schedule.');
        } finally {
            setSaving(false);
        }
    };

    /**
     * Which of the four bands a row in the running plan is in.
     *
     * `current` is the event we are IN - the most recent one to have started - and is distinct
     * from `passed`, which is everything before it. Only those two need the distinction: without
     * it the event actually underway looks identical to one that finished hours ago.
     */
    const rowState = (atMs: number): 'passed' | 'current' | 'next' | 'upcoming' => {
        const s = status();
        if (s.next && s.next.atMs === atMs) {return 'next';}
        if (s.current && s.current.atMs === atMs) {return 'current';}
        return atMs <= nowMs() ? 'passed' : 'upcoming';
    };

    const clockLabel = () => {
        const d = new Date(nowMs());
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
    };

    return (
        <div class="schedule-page" ref={rootEl}>
            {/* ---------------------------------------------------------------- countdown */}
            <div class={`schedule-page__countdown schedule-page__countdown--${urgency()}`}>
                {/* The only way into the editor. Everything that belongs to CHANGING the plan -
                    the plan bar, which plan is running, when it was last saved - stays behind
                    this, so read-only the page is just the clock and the running order. */}
                <Show when={!editing()}>
                    <button
                        type="button"
                        class="schedule-page__settings"
                        onClick={() => setEditing(true)}
                        title="Edit the schedule"
                        aria-label="Edit the schedule"
                    >
                        <FiSettings size={16} />
                    </button>
                </Show>

                <Show
                    when={status().next}
                    fallback={
                        <div class="schedule-page__countdown-idle">
                            <Show
                                when={status().items.length > 0}
                                fallback={
                                    <>
                                        {/* Two different nothings: no plan is running at all, or
                                            the one that is has no events in it yet. Saying
                                            "no plan" in the second case reads as a page that
                                            failed to load the plan it is plainly showing. */}
                                        <div class="schedule-page__countdown-label">
                                            {appliedActiveName()
                                                ? `No events in “${appliedActiveName()}”`
                                                : 'No plan running'}
                                        </div>
                                        <div class="schedule-page__countdown-hint">
                                            {editing()
                                                ? 'Add events below, then press Save.'
                                                : 'Press the settings icon to build the day’s plan.'}
                                        </div>
                                    </>
                                }
                            >
                                <div class="schedule-page__countdown-label">Plan complete</div>
                                <Show when={status().current}>
                                    {(item) => (
                                        <div class="schedule-page__countdown-hint">
                                            Last event: {item().name} at {item().time} (
                                            {formatElapsed(status().msSinceCurrent)} ago)
                                        </div>
                                    )}
                                </Show>
                            </Show>
                        </div>
                    }
                >
                    {(next) => (
                        <>
                            <div class="schedule-page__countdown-label">Next event in</div>
                            <div class="schedule-page__countdown-value">
                                {formatCountdown(status().msToNext)}
                            </div>
                            <div class="schedule-page__countdown-event">{next().name}</div>
                            <div class="schedule-page__countdown-at">scheduled {next().time}</div>
                        </>
                    )}
                </Show>

                <Show when={status().gapProgress !== null}>
                    <div class="schedule-page__progress" role="presentation">
                        <div
                            class="schedule-page__progress-fill"
                            style={{ width: `${((status().gapProgress ?? 0) * 100).toFixed(2)}%` }}
                        />
                    </div>
                </Show>

                <div class="schedule-page__countdown-footer">
                    <Show when={status().current}>
                        {(item) => (
                            <span class="schedule-page__since">
                                {item().name} started {formatElapsed(status().msSinceCurrent)} ago
                            </span>
                        )}
                    </Show>
                    <span class="schedule-page__clock" title="Local time on this machine">
                        {clockLabel()}
                    </span>
                </div>
            </div>

            {/* Everything below the countdown scrolls on its own, so the clock stays pinned to
                the head of the page however long the plan gets. */}
            <div class="schedule-page__body">
            {/* ---------------------------------------------------------- the running plan */}
            <Show when={loading()}>
                <div class="schedule-page__status">Loading schedule…</div>
            </Show>
            <Show when={loadError()}>
                <div class="schedule-page__error" role="alert">
                    {loadError()}
                </div>
            </Show>

            <Show when={!loading() && status().items.length > 0}>
                <div class="schedule-page__timeline">
                    <For each={status().items}>
                        {(item) => (
                            <div class={`schedule-row schedule-row--${rowState(item.atMs)}`}>
                                <span class="schedule-row__time">{item.time}</span>
                                <span class="schedule-row__name">{item.name}</span>
                                <span class="schedule-row__delta">
                                    {rowState(item.atMs) === 'passed'
                                        ? `-${formatElapsed(nowMs() - item.atMs)}`
                                        : `+${formatCountdown(item.atMs - nowMs())}`}
                                </span>
                            </div>
                        )}
                    </For>
                </div>
            </Show>

            {/* ------------------------------------------------------------------ editor */}
            {/* The WHOLE card is gated, not just its controls: read-only there is nothing here
                worth the room - which plan is running and when it was last saved are editing
                housekeeping, not something to put under a clock everyone is watching. Not
                indented, like __body above, so the diff stays readable. */}
            <Show when={editing()}>
            <div class="schedule-page__editor">
                <div class="schedule-page__editor-header">
                    <h3 class="schedule-page__editor-title">Daily plans</h3>
                    <div class="schedule-page__editor-actions">
                        <button
                            type="button"
                            class="btn btn-secondary"
                            onClick={addRow}
                            disabled={saving() || planIndex() < 0}
                        >
                            <FiPlus size={13} /> Add event
                        </button>
                        <button
                            type="button"
                            class="btn btn-secondary"
                            onClick={cancelEditing}
                            disabled={saving()}
                            title={
                                isDirty()
                                    ? 'Close the editor, discarding every change since the last save'
                                    : 'Close the editor'
                            }
                        >
                            Cancel
                        </button>
                        {/* Save appears only when there is something to save. A permanently
                            visible, permanently greyed Save invites a click that does nothing;
                            its absence says "no changes" more clearly than a disabled button.
                            `disabled` still covers the in-flight request, so a double click
                            cannot post twice. */}
                        <Show when={isDirty()}>
                            <button
                                type="button"
                                class="btn schedule-page__btn-save"
                                onClick={() => void savePlans()}
                                disabled={saving()}
                                title="Save these plans and run the one selected below"
                            >
                                {saving() ? 'Saving…' : 'Save'}
                            </button>
                        </Show>
                    </div>
                </div>

                <div class="schedule-page__editor-meta">
                    <Show when={isDirty()}>
                        <span class="schedule-page__badge schedule-page__badge--draft">
                            Unsaved changes
                        </span>
                    </Show>
                    <Show when={savedFlash()}>
                        <span class="schedule-page__badge schedule-page__badge--ok">Schedule saved</span>
                    </Show>
                    <Show when={invalidDraftCount() > 0}>
                        <span class="schedule-page__badge schedule-page__badge--warn">
                            {invalidDraftCount()} incomplete{' '}
                            {invalidDraftCount() === 1 ? 'row' : 'rows'} will not be saved
                        </span>
                    </Show>
                    <Show when={appliedAt() && !isDirty()}>
                        <span class="schedule-page__applied-at">
                            Saved {new Date(appliedAt() as string).toLocaleString()}
                        </span>
                    </Show>
                </div>

                <Show when={saveError()}>
                    <div class="schedule-page__error" role="alert">
                        {saveError()}
                    </div>
                </Show>

                {/* The plan bar: which plan is on screen, what it is called, and the three
                    ways to get another one. Everything here is a draft edit - no plan is
                    created, renamed or deleted in the database until Save. */}
                <div class="schedule-page__plans">
                    <label class="schedule-page__plans-label" for="schedule-plan-select">
                        Plan
                    </label>
                    <select
                        id="schedule-plan-select"
                        class="schedule-page__plans-select"
                        value={selectedPlanId() ?? ''}
                        onChange={(e) => {
                            setEditingRowId(null);
                            setOffsetError(null);
                            setSelectedPlanId(e.currentTarget.value || null);
                        }}
                        disabled={saving() || draft.plans.length === 0}
                        title="The plan shown below, and the one the countdown runs once you save"
                    >
                        <For each={draft.plans}>
                            {(plan) => (
                                <option value={plan.id}>
                                    {plan.name}
                                    {plan.id === appliedActiveId() ? ' — running' : ''}
                                </option>
                            )}
                        </For>
                    </select>

                    <input
                        type="text"
                        class="schedule-page__plans-name"
                        value={selectedPlan()?.name ?? ''}
                        placeholder="Plan name"
                        maxLength={80}
                        disabled={saving() || planIndex() < 0}
                        onInput={(e) => renamePlan(e.currentTarget.value)}
                        aria-label="Plan name"
                        title="Rename this plan"
                    />

                    <button
                        type="button"
                        class="btn btn-secondary"
                        onClick={createPlan}
                        disabled={saving()}
                        title="Start a new, empty plan"
                    >
                        <FiPlus size={13} /> New
                    </button>
                    <button
                        type="button"
                        class="btn btn-secondary"
                        onClick={duplicatePlan}
                        disabled={saving() || planIndex() < 0}
                        title="Copy this plan, events and all"
                    >
                        <FiCopy size={13} /> Copy
                    </button>
                    <button
                        type="button"
                        class="btn btn-secondary"
                        onClick={deletePlan}
                        disabled={saving() || planIndex() < 0}
                        title="Remove this plan from the draft. Nothing is deleted until you save."
                    >
                        <FiTrash2 size={13} /> Delete
                    </button>
                </div>

                {/* Said out loud because the dropdown is doing two jobs: it picks what you are
                    editing AND what the room's countdown will follow. */}
                <Show when={planIndex() >= 0 && selectedPlanId() !== appliedActiveId()}>
                    <div class="schedule-page__plans-hint">
                        Saving will run “{selectedPlan()?.name}” instead of{' '}
                        {appliedActiveName() ? `“${appliedActiveName()}”` : 'nothing'}.
                    </div>
                </Show>

                {/* Offset: postpone or pull forward the whole plan in one go. Edits the draft,
                    so it is reviewed and saved like any other change. */}
                <div class="schedule-page__offset">
                    <label class="schedule-page__offset-label" for="schedule-offset-minutes">
                        Offset all by
                    </label>
                    <input
                        id="schedule-offset-minutes"
                        type="number"
                        class="schedule-page__offset-input"
                        min={1}
                        max={1439}
                        step={1}
                        value={offsetMinutes()}
                        onInput={(e) => {
                            setOffsetMinutes(Number(e.currentTarget.value));
                            setOffsetError(null);
                        }}
                        aria-label="Offset in minutes"
                    />
                    <span class="schedule-page__offset-unit">min</span>
                    <button
                        type="button"
                        class="btn btn-secondary"
                        onClick={() => shiftAll(-1)}
                        disabled={saving() || draftItems().length === 0}
                        title="Move every event earlier"
                    >
                        − Earlier
                    </button>
                    <button
                        type="button"
                        class="btn btn-secondary"
                        onClick={() => shiftAll(1)}
                        disabled={saving() || draftItems().length === 0}
                        title="Move every event later"
                    >
                        + Later
                    </button>
                </div>

                <Show when={offsetError()}>
                    <div class="schedule-page__error" role="alert">
                        {offsetError()}
                    </div>
                </Show>

                <Show
                    when={draftItems().length > 0}
                    fallback={
                        <div class="schedule-page__empty">
                            {planIndex() < 0
                                ? 'No plans yet. Press “New” to start one.'
                                : 'No events yet. Press “Add event” to start building the day.'}
                        </div>
                    }
                >
                <table class="schedule-page__table" aria-label="Schedule events">
                    <thead>
                        <tr>
                            <th scope="col">Time</th>
                            <th scope="col">Event</th>
                            <th scope="col"><span aria-label="Remove" /></th>
                        </tr>
                    </thead>
                    <tbody>
                        <For each={draftItems()}>
                            {(row) => (
                                <tr
                                    class={
                                        // Same rule as the badge: never mark the row under the
                                        // caret, or the field turns red as you reach for it.
                                        editingRowId() !== row.id && isRowIncomplete(row)
                                            ? 'schedule-edit-row schedule-edit-row--invalid'
                                            : 'schedule-edit-row'
                                    }
                                >
                                    <td>
                                        <input
                                            type="time"
                                            class="schedule-edit-row__time"
                                            // `value`, not a normalize() call: a <input type="time">
                                            // only ever emits "HH:MM" or "", and parseScheduleObject
                                            // already canonicalised anything that came from the API,
                                            // so re-normalising here only risks fighting the picker.
                                            value={row.time}
                                            onFocus={() => {
                                                setEditingRowId(row.id);
                                                // Snapshot before the first keystroke lands.
                                                timeBeforeEdit = { id: row.id, time: row.time };
                                            }}
                                            onBlur={() =>
                                                setEditingRowId((cur) => (cur === row.id ? null : cur))
                                            }
                                            onInput={(e) =>
                                                updateRow(row.id, 'time', e.currentTarget.value)
                                            }
                                            onChange={(e) =>
                                                commitTimeEdit(row.id, e.currentTarget.value)
                                            }
                                            aria-label="Event time"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            class="schedule-edit-row__name"
                                            value={row.name}
                                            placeholder="Event name"
                                            maxLength={120}
                                            onFocus={() => setEditingRowId(row.id)}
                                            onBlur={() =>
                                                setEditingRowId((cur) => (cur === row.id ? null : cur))
                                            }
                                            onInput={(e) =>
                                                updateRow(row.id, 'name', e.currentTarget.value)
                                            }
                                            aria-label="Event name"
                                        />
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            class="schedule-edit-row__remove"
                                            onClick={() => removeRow(row.id)}
                                            aria-label={`Remove ${row.name || 'event'}`}
                                            title="Remove this event"
                                        >
                                            <FiTrash2 size={14} />
                                        </button>
                                    </td>
                                </tr>
                            )}
                        </For>
                    </tbody>
                </table>
                </Show>
            </div>
            </Show>
            {/* /schedule-page__editor is gated on editing() - see the comment above it */}
            {/* /schedule-page__body - the scrolling half; the countdown above it stays pinned */}
            </div>
        </div>
    );
}
